/**
 * SegmentQuickSave — Popover to save current product filters as a named segment.
 *
 * WCAG 2.1 Level AA + BFSG konform
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ProductTableFilters } from '@/hooks/useProductTableConfig';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/v2/ui/Popover';
import { Button } from '@/components/v2/ui/Button';
import { Input } from '@/components/v2/ui/Input';

// ============================================================================
// Types
// ============================================================================

interface SegmentQuickSaveProps {
  currentFilters: ProductTableFilters;
  onSave: (name: string, filters: ProductTableFilters) => void;
}

// ============================================================================
// Component
// ============================================================================

export function SegmentQuickSave({ currentFilters, onSave }: SegmentQuickSaveProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');

  function handleSubmit() {
    const trimmed = name.trim();
    if (!trimmed) return;
    onSave(trimmed, currentFilters);
    setName('');
    setOpen(false);
  }

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (!next) setName('');
        setOpen(next);
      }}
    >
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" data-testid="quicksave-trigger">
          {t('productSegments.quickSave')}
        </Button>
      </PopoverTrigger>
      <PopoverContent>
        <div className="flex flex-col gap-3">
          <Input
            data-testid="quicksave-name"
            label={t('productSegments.builder.name')}
            placeholder={t('productSegments.builder.name')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
          />
          <Button
            data-testid="quicksave-submit"
            variant="primary"
            size="sm"
            onClick={handleSubmit}
          >
            {t('productSegments.builder.save')}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
